import { fetchAreas } from '@/lib/request/fetchAreas';
import type { Areas } from '@/lib/request/types/area';
import { defineStore } from 'pinia';
import { useKioskConfigStore } from '@/stores/kioskConfig';

export interface AreaStoreStatus {
  isLoading: boolean;
}

export interface AreaStoreErrors {
  loadingError: Error | null;
}

export interface AreaStore {
  data: {
    location: Areas;
    all: Areas;
  };
  status: AreaStoreStatus;
  errors: AreaStoreErrors;
}

export const useAreaStore = defineStore({
  id: 'area',
  state: (): AreaStore => ({
    data: {
      location: [],
      all: [],
    },
    status: {
      isLoading: false,
    },
    errors: {
      loadingError: null,
    },
  }),
  getters: {
    isLoading: (state) => {
      return state.status.isLoading;
    },
    searchAreas(state) {
      const kioskConfigStore = useKioskConfigStore();
      return (searchTerm: string) => {
        const searchTerms = searchTerm.split(' ').filter((val) => val);

        return state.data.location.filter((area) => {
          const searchableText = kioskConfigStore.data.KioskAppConfigs
            .ENABLE_MULTI_LOCATIONS
            ? area.OperationalUnitName.toLowerCase() +
              ' (' +
              area.CompanyName.toLowerCase() +
              ')'
            : area.OperationalUnitName.toLowerCase();
          return searchTerms.every((term) =>
            searchableText.includes(term.toLowerCase())
          );
        });
      };
    },
    getAreaById(state) {
      return (areaId: number) =>
        state.data.all.find((area) => area.Id === areaId);
    },
    getAreaByCompanyAndId(state) {
      return (comapnyId: number, areaId: number) => {
        const comapnyAreas = state.data.all.filter(
          (area) => area.Company === comapnyId
        );
        return comapnyAreas.find((area) => area.Id === areaId);
      };
    },
  },
  actions: {
    async load() {
      const kioskConfigStore = useKioskConfigStore();

      this.errors.loadingError = null;
      this.status.isLoading = true;
      try {
        const areas = await fetchAreas();
        this.data.all = areas;
        this.data.location = kioskConfigStore.data.KioskAppConfigs
          .ENABLE_MULTI_LOCATIONS
          ? areas
          : areas.filter(
              (area) => area.Company === kioskConfigStore.data.KioskCompanyId
            );
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },
  },
});
